import type { AlphaStop, Attachment, AttachmentShape, Blend, ColourStop, EffectPreset, Range2, SizeEntry } from "./types.ts";

/**
 * The engine-neutral numbers an emitter is made from (ADR-141 decision F12): a pure mapping from a preset and an attachment,
 * tested without a browser, that the renderer applies to its particle engine.
 */

/**
 * The detail an emitter runs at: 1 is Full, 0.5 is Reduced (rate, burst and pool halve).
 */
export type EffectDetail = 1 | 0.5;

/**
 * One emitter's settings, one per sub-kind of a particle preset.
 */
export interface EmitterSettings {
  readonly subKind: number;
  readonly ratePerSecond: number;
  readonly burst: number;
  readonly maxParticles: number;
  readonly life: Range2;
  readonly sizes: readonly SizeEntry[];
  readonly speed: Range2;
  readonly angle: Range2;
  readonly gravity: number;
  readonly streak: number;
  readonly swayAmplitude: number;
  readonly swayHz: number;
  readonly colours: readonly ColourStop[];
  readonly alpha: readonly AlphaStop[];
  readonly blend: Blend;
  readonly emissive: boolean;
  readonly shape: AttachmentShape;
  readonly startDelayMs: number;
  readonly behind: boolean;
}

/**
 * Splits a packed colour into channels.
 *
 * @param rgb 0xRRGGBB.
 * @returns Red, green and blue, 0 to 255.
 */
function channels(rgb: number): [number, number, number] {
  return [(rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff];
}

/**
 * Finds the segment of a ramp a point of life falls in.
 *
 * @param stops The ramp's stops by `at`.
 * @param u A point of life.
 * @returns The index of the segment's first stop and the share along it, or null to hold an end.
 */
function segmentAt(stops: readonly { readonly at: number }[], u: number): { index: number; t: number } | { end: 0 | 1 } {
  const last = stops.length - 1;
  if (u <= (stops[0] as { at: number }).at) return { end: 0 };
  if (u >= (stops[last] as { at: number }).at) return { end: 1 };
  let index = 0;
  while (index < last - 1 && u > (stops[index + 1] as { at: number }).at) index += 1;
  const from = (stops[index] as { at: number }).at;
  const to = (stops[index + 1] as { at: number }).at;
  return { index, t: (u - from) / (to - from) };
}

/**
 * The colour of a ramp at a point of a particle's life, each channel interpolated and rounded.
 *
 * @param stops A valid colour ramp.
 * @param u The point of life, 0 to 1; outside it holds the end.
 * @returns 0xRRGGBB.
 */
export function rampRgbAt(stops: readonly ColourStop[], u: number): number {
  const seg = segmentAt(stops, u);
  if ("end" in seg) return (stops[seg.end === 0 ? 0 : stops.length - 1] as ColourStop).rgb;
  const a = channels((stops[seg.index] as ColourStop).rgb);
  const b = channels((stops[seg.index + 1] as ColourStop).rgb);
  const mix = (i: 0 | 1 | 2): number => Math.round(a[i] + (b[i] - a[i]) * seg.t);
  return (mix(0) << 16) | (mix(1) << 8) | mix(2);
}

/**
 * The alpha of a ramp at a point of a particle's life.
 *
 * @param stops A valid alpha ramp.
 * @param u The point of life, 0 to 1; outside it holds the end.
 * @returns Alpha, 0 to 1.
 */
export function rampAlphaAt(stops: readonly AlphaStop[], u: number): number {
  const seg = segmentAt(stops, u);
  if ("end" in seg) return (stops[seg.end === 0 ? 0 : stops.length - 1] as AlphaStop).value;
  const a = (stops[seg.index] as AlphaStop).value;
  const b = (stops[seg.index + 1] as AlphaStop).value;
  return a + (b - a) * seg.t;
}

/**
 * Samples a colour ramp at evenly spaced points, for an engine that interpolates a list of colours evenly over life.
 *
 * @param stops A valid colour ramp.
 * @param count How many samples, at least 2.
 * @returns The sampled colours from the first stop to the last.
 */
export function sampleRgb(stops: readonly ColourStop[], count: number): number[] {
  return Array.from({ length: count }, (_, i) => rampRgbAt(stops, i / (count - 1)));
}

/**
 * Samples an alpha ramp at evenly spaced points.
 *
 * @param stops A valid alpha ramp.
 * @param count How many samples, at least 2.
 * @returns The sampled alphas from the first stop to the last.
 */
export function sampleAlpha(stops: readonly AlphaStop[], count: number): number[] {
  return Array.from({ length: count }, (_, i) => rampAlphaAt(stops, i / (count - 1)));
}

/**
 * Turns a particle preset and an attachment into one set of emitter settings per sub-kind.
 *
 * @param preset A valid preset; a light gives none.
 * @param attachment Where the effect comes from.
 * @param detail 1 for Full, 0.5 for Reduced: the rate, burst and pool scale by it.
 * @returns The settings, in the order of the preset's sub-kinds.
 */
export function emitterSettings(preset: EffectPreset, attachment: Attachment, detail: EffectDetail = 1): EmitterSettings[] {
  if (preset.kind !== "particles") return [];
  return preset.kinds.map((kind, subKind) => {
    const ratePerSecond = preset.rate * kind.share * detail;
    const burst = Math.round(preset.burst * kind.share * detail);
    return {
      subKind,
      ratePerSecond,
      burst,
      maxParticles: Math.ceil((ratePerSecond * kind.life[1]) / 1000) + burst,
      life: kind.life,
      sizes: kind.size,
      speed: kind.speed,
      angle: [kind.direction - kind.spread / 2, kind.direction + kind.spread / 2],
      gravity: kind.gravity,
      streak: kind.streak ?? 0,
      swayAmplitude: kind.sway.amplitude,
      swayHz: kind.sway.hz,
      colours: kind.colours,
      alpha: kind.alpha,
      blend: kind.blend,
      emissive: kind.emissive,
      shape: attachment.shape,
      startDelayMs: attachment.offset ?? 0,
      behind: attachment.z === "behind",
    };
  });
}
